import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { OrganisationContext, UserContext } from '../../../context';
import {
  ChannelAppTabAction,
  OrganisationTagType,
  StoryCardSection,
  storyCardApi,
} from '../../../services';
import {
  Filter,
  FilterGroup,
  FilterValue,
  STORY_TAB_ITEMS_ADMIN_COOKIE_KEY,
  cookieOptions,
  optArrItem,
  useAppDispatch,
  useAppTabsLabel,
  useAuthorsFilter,
  useCardCategoryFilter,
  useCardStatusFilter,
  useCardTypeFilter,
  useDateRangeFilter,
  useFilter,
  useSourcesFilter,
  useTags,
  useTagsFilter,
} from '../../../shared';
import { getStoryFeed } from '../../../slices';

import classes from './StoryFeedItemsAdminRightSide.module.scss';

export const StoryFeedItemsAdminRightSide: FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [fetchAuthors] = storyCardApi.endpoints.storyCardAuthorsFiltersByStoryId.useLazyQuery();

  const [fetchSources] = storyCardApi.endpoints.storyCardSourcesFiltersByChannelId.useLazyQuery();

  const { getAppTabsLabel } = useAppTabsLabel();

  const { getGroupValue, getDateRangeValue } = useFilter();

  const { hasInternalTags, hasExternalTags, hasExternalStatusTags } = useTags();

  const hasCategories = Boolean(useContext(OrganisationContext).organisation.categories.length);

  const { channelId } = useContext(UserContext).userInfo.userData;

  const [cookies, setCookie] = useCookies([STORY_TAB_ITEMS_ADMIN_COOKIE_KEY]);

  const [filter, setFilter] = useState<FilterValue[]>(
    cookies[STORY_TAB_ITEMS_ADMIN_COOKIE_KEY]?.filter ?? []
  );

  const storyId = Number(useParams().storyId);

  const { group: dateRangeGroup } = useDateRangeFilter({ filter });

  const { group: authorsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchAuthors,
    fetchItemsQueryArgs: { storyId },
  });

  const { group: sourcesGroup } = useSourcesFilter({
    filter,
    fetchItemsQuery: fetchSources,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: categoryGroup } = useCardCategoryFilter({ filter });

  const { group: internalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.INTERNAL],
  });

  const { group: externalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL],
  });

  const { group: externalStatusTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL_STATUS],
  });

  const { group: cardTypeGroup } = useCardTypeFilter();

  const { group: cardStatusGroup } = useCardStatusFilter();

  const groups: FilterGroup[] = useMemo(
    () => [
      {
        id: 'sections',
        title: t('common.sections'),
        multiCheck: true,
        allowReset: true,
        items: [
          {
            id: StoryCardSection.WEB_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.web')}`,
            value: StoryCardSection.WEB_FEED,
          },
          {
            id: StoryCardSection.MOBILE_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.mobile')}`,
            value: StoryCardSection.MOBILE_FEED,
          },
        ],
      },
      ...optArrItem(hasInternalTags, internalTagsGroup),
      ...optArrItem(hasExternalTags, externalTagsGroup),
      ...optArrItem(hasExternalStatusTags, externalStatusTagsGroup),
      ...optArrItem(hasCategories, categoryGroup),
      authorsGroup,
      sourcesGroup,
      dateRangeGroup,
      cardTypeGroup,
      cardStatusGroup,
    ],
    [
      authorsGroup,
      cardStatusGroup,
      cardTypeGroup,
      categoryGroup,
      dateRangeGroup,
      externalStatusTagsGroup,
      externalTagsGroup,
      getAppTabsLabel,
      hasCategories,
      hasExternalStatusTags,
      hasExternalTags,
      hasInternalTags,
      internalTagsGroup,
      sourcesGroup,
      t,
    ]
  );

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      if (!storyId) {
        return;
      }

      const commonIdProps = { filter, multiCheck: true, propToMap: 'id' };

      const authorId = getGroupValue({ ...commonIdProps, groupId: authorsGroup.id });

      const sourceName = getGroupValue({ ...commonIdProps, groupId: sourcesGroup.id });

      const categoryId = getGroupValue({ ...commonIdProps, groupId: categoryGroup.id });

      const internalTagsId = getGroupValue({ ...commonIdProps, groupId: internalTagsGroup.id });

      const externalTagsId = getGroupValue({ ...commonIdProps, groupId: externalTagsGroup.id });

      const externalStatusTagsId = getGroupValue({
        ...commonIdProps,
        groupId: externalStatusTagsGroup.id,
      });

      const section = getGroupValue({ filter, groupId: 'sections', multiCheck: true });

      const storyCardType = getGroupValue({ filter, groupId: cardTypeGroup.id, multiCheck: true });

      const status = getGroupValue({ filter, groupId: cardStatusGroup.id, multiCheck: true });

      const { dateRange, from, to } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      const common = {
        ...(authorId?.length && { authorId }),
        ...(sourceName?.length && { sourceName }),
        ...(categoryId?.length && { categoryId }),
        ...((internalTagsId?.length || externalTagsId?.length || externalStatusTagsId?.length) && {
          organisationTags: { internalTagsId, externalTagsId, externalStatusTagsId },
        }),
        ...(section?.length && { section }),
        ...(storyCardType?.length && { storyCardType }),
        ...(status?.length && { status }),
      };

      const requestFilterToSave = { ...common, dateRange };

      const requestFilter = { ...common, ...(from && { from }), ...(to && { to }) };

      setCookie(
        STORY_TAB_ITEMS_ADMIN_COOKIE_KEY,
        { requestFilter: requestFilterToSave, filter },
        cookieOptions()
      );

      dispatch(getStoryFeed({ storyId: Number(storyId), filter: requestFilter }));

      setFilter(filter);
    },
    [
      authorsGroup.id,
      cardStatusGroup.id,
      cardTypeGroup.id,
      categoryGroup.id,
      dateRangeGroup.id,
      dispatch,
      externalStatusTagsGroup.id,
      externalTagsGroup.id,
      getDateRangeValue,
      getGroupValue,
      internalTagsGroup.id,
      setCookie,
      sourcesGroup.id,
      storyId,
    ]
  );

  return (
    <div className={classes['story-feed-items-right-side']}>
      <Filter filter={filter} groups={groups} onChange={filterChange} resetPageScrollOnChange />
    </div>
  );
};
